import { useForm } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { ApiError } from '@/lib/api'
import {
  SCOPED_PLATFORMS,
  formatScope,
  moduleSchema,
  parseScope,
  useCreateModule,
  useSaveModule,
  type Module,
  type ModuleInput,
} from '@/features/i18n/api'

/**
 * Add or edit one module.
 *
 * The module dictionary exists so a module can never be free text (D-12). A
 * typo'd module fails in a way nothing catches: the key saves, the publish
 * succeeds, the file uploads under `hoem/`, and no client ever requests it —
 * nobody finds out until a user reports missing copy. Which makes this screen
 * the one place a typo can still get in, so the code is validated here and
 * frozen afterwards.
 */

interface ModuleEditorProps {
  /** Absent for a new module. */
  module?: Module
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ModuleEditor({ module, open, onOpenChange }: ModuleEditorProps) {
  const isNew = module === undefined
  const createModule = useCreateModule()
  const saveModule = useSaveModule()

  const form = useForm<ModuleInput>({
    resolver: standardSchemaResolver(moduleSchema),
    defaultValues: {
      code: module?.code ?? '',
      display_name: module?.display_name ?? '',
      platform_scope: module?.platform_scope ?? 'all',
      enabled: module?.enabled ?? true,
      sort_weight: module?.sort_weight ?? 50,
    },
  })

  const scope = form.watch('platform_scope')
  const selected = parseScope(scope)
  const unrestricted = selected.length === 0

  function toggleScope(platform: string, checked: boolean) {
    const next = checked
      ? [...selected, platform].filter((value, index, all) => all.indexOf(value) === index)
      : selected.filter((value) => value !== platform)
    // Unticking the last one falls back to unrestricted rather than to an empty
    // list. An empty `platform_scope` string means "all" to the backend anyway,
    // so the alternative is a control that looks restrictive and is not.
    form.setValue('platform_scope', formatScope(next), { shouldDirty: true })
  }

  async function onSubmit(values: ModuleInput) {
    try {
      if (isNew) {
        await createModule.mutateAsync({ ...values, code: values.code.toLowerCase() })
        toast.success(`${values.display_name} added.`)
      } else {
        await saveModule.mutateAsync({ id: module.id, input: { ...values, code: module.code } })
        toast.success(`${values.display_name} updated.`)
      }
      onOpenChange(false)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not save the module.')
    }
  }

  const saving = createModule.isPending || saveModule.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add module' : `Edit ${module.display_name}`}</DialogTitle>
          <DialogDescription>
            Modules group keys, and become a path segment in every published file.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="code">Module code</Label>
              <Input
                id="code"
                {...form.register('code')}
                disabled={!isNew}
                spellCheck={false}
                className="font-mono text-xs"
                placeholder="market"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {isNew
                  ? 'Becomes a filename. Check the spelling — it cannot be changed later.'
                  : 'Fixed — renaming it would strand every file already published under it.'}
              </p>
              {form.formState.errors.code && (
                <p className="mt-1 text-xs text-destructive">{form.formState.errors.code.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="display_name">Display name</Label>
              <Input id="display_name" {...form.register('display_name')} placeholder="Market" />
              <p className="mt-1 text-xs text-muted-foreground">
                What the module dropdown shows to staff.
              </p>
              {form.formState.errors.display_name && (
                <p className="mt-1 text-xs text-destructive">
                  {form.formState.errors.display_name.message}
                </p>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-border-strong bg-secondary/50 p-3">
            <Label>Platforms</Label>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Which platforms may file keys under this module.
            </p>

            <label className="mt-2.5 flex items-center justify-between gap-3 text-[13px]">
              <span>
                <b>Any platform</b>
                <span className="block text-xs text-muted-foreground">
                  The usual choice. Restrict only when a module genuinely does not belong on a
                  platform — <code>seo</code> on web, say.
                </span>
              </span>
              <Switch
                checked={unrestricted}
                onCheckedChange={(checked) =>
                  // Restricting defaults to `web` rather than to nothing, so the
                  // control never sits in a state that reads as restricted while
                  // behaving as unrestricted.
                  form.setValue('platform_scope', checked ? 'all' : 'web', { shouldDirty: true })
                }
              />
            </label>

            {!unrestricted && (
              <div className="mt-2.5 border-t border-border pt-2.5">
                <div className="flex flex-wrap gap-3">
                  {SCOPED_PLATFORMS.map((platform) => (
                    <label key={platform} className="flex items-center gap-1.5 text-[13px]">
                      <input
                        type="checkbox"
                        className="size-3.5 accent-[var(--primary)]"
                        checked={selected.includes(platform)}
                        onChange={(event) => toggleScope(platform, event.target.checked)}
                      />
                      <code className="font-mono text-xs">{platform}</code>
                    </label>
                  ))}
                </div>

                {/*
                  This is the part that surprises people. Ticking all three is
                  NOT the same as "Any platform": a key whose own platform is
                  `all` exports into every platform's files, so it needs a module
                  that is itself unrestricted. Saying so here is cheaper than the
                  1001 it otherwise produces at save time on the Messages screen.
                */}
                <div className="mt-2.5 flex items-start gap-2 rounded-md bg-warning-soft p-2 text-xs text-[#a9760c]">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                  <div>
                    Restricted modules reject keys filed under platform <code>all</code>, even if
                    every platform is ticked — those keys ship to all platforms, so their module has
                    to be unrestricted. Use <b>Any platform</b> if you want that.
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-end gap-6">
            <div>
              <Label htmlFor="sort_weight">Dropdown order</Label>
              <Input
                id="sort_weight"
                type="number"
                inputMode="numeric"
                {...form.register('sort_weight')}
                className="w-32"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Higher appears first. Existing modules use 100 down to 10.
              </p>
              {form.formState.errors.sort_weight && (
                <p className="mt-1 text-xs text-destructive">
                  {form.formState.errors.sort_weight.message}
                </p>
              )}
            </div>

            <label className="flex items-center gap-2 pb-6 text-[13px]">
              <Switch
                checked={form.watch('enabled')}
                onCheckedChange={(checked) =>
                  form.setValue('enabled', checked, { shouldDirty: true })
                }
              />
              <span>
                <b>Enabled</b>
                <span className="block text-xs text-muted-foreground">
                  Offered in the module dropdown.
                </span>
              </span>
            </label>
          </div>

          {!isNew && module.enabled && !form.watch('enabled') && (
            <div className="flex items-start gap-2 rounded-md bg-warning-soft p-2.5 text-xs text-[#a9760c]">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              <div>
                Keys already filed under <code>{module.code}</code> keep their module and stay in
                the database — they just stop being offered for new keys. Re-enabling restores it,
                so this is reversible.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Add module' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
